import React, { useMemo, useState } from 'react';import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, X, Compass, AlertTriangle, Plus } from 'lucide-react';

export const MAX_RELATED = 3;
export const CAREER_FIELDS = {
  career_slug: '', related_career_slugs: [], career_request_name: '', career_request_description: '',
};

const pickerCls = 'rounded-xl h-10 border-gray-200 pl-9';

function CareerPicker({ careers, exclude, onPick, placeholder, testId }) {
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const scored = careers
      .filter(c => !exclude.includes(c.slug))
      .map(c => {
        if (!q) return [2, c];
        const name = c.name.toLowerCase();
        if (name.startsWith(q)) return [0, c];
        if (name.includes(q)) return [1, c];
        if ((c.category || '').toLowerCase().includes(q)) return [3, c];
        return [9, c];
      })
      .filter(([rank]) => rank !== 9);
    scored.sort((a, b) => a[0] - b[0]);
    return scored.slice(0, 40).map(([, c]) => c);
  }, [careers, exclude, query]);

  return (
    <div>
      <div className="relative">
        <Search className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-3" />
        <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder}
          className={pickerCls} data-testid={`${testId}-search`} />
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-50" data-testid={`${testId}-results`}>
        {matches.map(c => (
          <button key={c.slug} type="button" onClick={() => { onPick(c.slug); setQuery(''); }}
            className="w-full text-left px-3 py-2 hover:bg-[#FFF6F4] transition-colors"
            data-testid={`${testId}-option-${c.slug}`}>
            <span className="text-sm text-[#1F2937]">{c.name}</span>
            {c.category && <span className="text-[11px] text-[#9CA3AF] ml-2">{c.category}</span>}
          </button>
        ))}
        {matches.length === 0 && (
          <p className="px-3 py-3 text-xs text-[#9CA3AF]" data-testid={`${testId}-empty`}>
            No career in the library matches “{query}”.
          </p>
        )}
      </div>
    </div>
  );
}

/** Mandatory career mapping: one primary career from the CareersPOV library, optional related
 *  careers, and a new-career request when the library has nothing suitable. */
export default function CareerMapping({ form, set, careers, status }) {
  const bySlug = useMemo(() => Object.fromEntries(careers.map(c => [c.slug, c])), [careers]);
  const related = form.related_career_slugs || [];
  const [asking, setAsking] = useState(Boolean(form.career_request_name));
  const requesting = asking;
  const primary = form.career_slug ? bySlug[form.career_slug] : null;

  const startRequest = () => {
    set('career_slug', '');
    setAsking(true);
  };
  const cancelRequest = () => {
    set('career_request_name', '');
    set('career_request_description', '');
    setAsking(false);
  };

  return (
    <div className="bg-[#FAFBFC] rounded-2xl border border-[#EFF0F2] p-4 space-y-4" data-testid="career-mapping">
      <div className="flex items-start gap-2">
        <Compass className="h-4 w-4 text-[#F97D6A] mt-0.5" />
        <div>
          <p className="text-sm font-bold text-[#1F2937] font-['Outfit']">
            Career from the CareersPOV library <span className="text-[#F97D6A]">*</span>
          </p>
          <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">
            Every published internship is linked to one career so students can read the full career
            profile alongside it.
          </p>
        </div>
      </div>

      {!requesting && (
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">Primary career</Label>
          {primary ? (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid="primary-career-selected">
              <Badge className="border-0 bg-[#FFF0ED] text-[#F06651] text-xs py-1 px-2.5">{primary.name}</Badge>
              <button type="button" onClick={() => set('career_slug', '')}
                className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] underline"
                data-testid="change-primary-career">Change</button>
            </div>
          ) : (
            <div className="mt-1.5">
              <CareerPicker careers={careers} exclude={related} placeholder="Search the career library"
                onPick={slug => set('career_slug', slug)} testId="primary-career" />
            </div>
          )}
        </div>
      )}

      {primary && !requesting && (
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">
            Related careers <span className="text-[#9CA3AF] font-normal">(optional, up to {MAX_RELATED})</span>
          </Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="related-careers">
            {related.map(slug => (
              <Badge key={slug} className="border-0 bg-white text-[#4B5563] text-[11px] py-1 pl-2.5 pr-1.5 flex items-center gap-1" data-testid={`related-career-${slug}`}>
                {bySlug[slug]?.name || slug}
                <button type="button" onClick={() => set('related_career_slugs', related.filter(s => s !== slug))}
                  className="text-[#9CA3AF] hover:text-[#F06651]" data-testid={`remove-related-${slug}`}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {related.length === 0 && <span className="text-[11px] text-[#9CA3AF]">None added</span>}
          </div>
          {related.length < MAX_RELATED && (
            <div className="mt-2">
              <CareerPicker careers={careers} exclude={[form.career_slug, ...related]}
                placeholder="Add a related career" testId="related-career"
                onPick={slug => set('related_career_slugs', [...related, slug])} />
            </div>
          )}
        </div>
      )}

      {!requesting ? (
        <Button type="button" variant="outline" size="sm" onClick={startRequest}
          className="rounded-xl text-xs border-gray-200 h-9" data-testid="request-career-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />Request a new career
        </Button>
      ) : (
        <div className="space-y-3" data-testid="career-request-block">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 leading-relaxed">
              You can still submit this internship to the school. It will show as
              <strong> Pending Career Review</strong> until CareersPOV adds or maps the career, and it
              can only be published once that is resolved.
            </p>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Proposed career name *</Label>
            <Input value={form.career_request_name} onChange={e => set('career_request_name', e.target.value)}
              placeholder="e.g. Drone Survey Technician" className="mt-1.5 rounded-xl h-10 border-gray-200"
              data-testid="career-request-name" />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Short description *</Label>
            <Textarea value={form.career_request_description}
              onChange={e => set('career_request_description', e.target.value)}
              placeholder="What people in this career actually do, in a line or two"
              className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] text-sm resize-none"
              data-testid="career-request-description" />
          </div>
          <button type="button" onClick={cancelRequest}
            className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] underline"
            data-testid="cancel-career-request">
            Pick a career from the library instead
          </button>
        </div>
      )}

      {status === 'pending' && (
        <p className="text-[11px] text-amber-700 flex items-center gap-1.5" data-testid="career-status-pending">
          <AlertTriangle className="h-3.5 w-3.5" />Pending Career Review with CareersPOV.
        </p>
      )}
      {status === 'rejected' && (
        <p className="text-[11px] text-red-600 flex items-center gap-1.5" data-testid="career-status-rejected">
          <AlertTriangle className="h-3.5 w-3.5" />CareersPOV did not add this career — link one from the library.
        </p>
      )}
    </div>
  );
}
